import { FC, ReactNode } from "react"
import styles from "./SupplierAccountCard.module.scss"
import BtButton from "../../bt-button/bt-button"
import { isEmpty } from "lodash"
import TooltipContainer from "../../../components/shared/TooltipContainer/TooltipContainer"
import { logMessage } from "../../../services/service-logs";
import { SlackChannels } from "../../../util/appEnum"
import { useTranslation, Trans } from "react-i18next"
import { logEvent } from "../../../services/eventsService"
import appState from "../../../state/AppStateContainer"

export type SupplierStatus = {
  organizationNumber?: string,
  numOfUnansweredQuotations?: number,
  numOfQuotationsLast12Months?: number,
  numOfOrganizationsSupplierIsMissing?: number,
  notPartOfSalesOrg?: boolean,
  noSupplierProfile?: boolean,
  orgSlug?: string,
  adminUsers?: {
    name: string,
    email: string,
  }[],
}

type Props = {
  supplierStatus?: SupplierStatus,
}

export const SupplierAccountCard: FC<Props> = ({ supplierStatus }) => {
  const [t] = useTranslation('quotation', { keyPrefix: 'supplierAccount' });

  if (isEmpty(supplierStatus)) return null;

  let text: ReactNode = null;
  let buttonText = '';

  if (supplierStatus?.numOfUnansweredQuotations) {
    text = <span><Trans t={t} i18nKey="info.numOfUnansweredQuotations" values={{count: supplierStatus?.numOfUnansweredQuotations }} components={{b: <b />}} /></span>;
    buttonText = t('button.createAccount', 'Skapa konto');
  } else if (supplierStatus?.numOfQuotationsLast12Months) {
    text = <span><Trans t={t} i18nKey="info.numOfQuotationsLast12Months" values={{count: supplierStatus?.numOfQuotationsLast12Months }} components={{b: <b />}} /></span>;
    buttonText = t('button.createAccount', 'Skapa konto');
  } else if (supplierStatus?.numOfOrganizationsSupplierIsMissing) {
    text = <span><Trans t={t} i18nKey="info.numOfOrganizationsSupplierIsMissing" values={{count: supplierStatus?.numOfOrganizationsSupplierIsMissing }} components={{b: <b />}} /></span>;
    buttonText = t('button.createAccount', 'Skapa konto');
  } else if (supplierStatus?.notPartOfSalesOrg) {
    text = <div>
      <span><Trans
        t={t}
        i18nKey="info.notPartOfSalesOrg"
        components={{administrators: <TooltipContainer
          renderReferenceComponent={(className, ref) => (
            <span className={className}
              ref={ref}
              style={{ borderBottom: '1px dotted var(--light-blue-800)', cursor: 'default' }}
            >{t('info.notPartOfSalesOrgAdministrators', 'administratörerna')}</span>
          )}
        >
          {supplierStatus?.adminUsers && supplierStatus?.adminUsers?.length > 0 && (
            <ul>
              {supplierStatus.adminUsers.map((adminUser) => <li>{adminUser.name} ({adminUser.email})</li>)}
            </ul>
          )}
        </TooltipContainer>}}
      /></span>
    </div>;
  } else if (supplierStatus?.noSupplierProfile) {
    text = <span>{t('info.noSupplierProfile', 'Du har ett leverantörskonto, men leverantörspresentationen är inte ifylld. Ta kontroll över hur ert företag presenteras för inköpare genom att fylla i presentationen.')}</span>;
    buttonText = t('button.addPresentation', 'Fyll i presentation');
  }

  const onClick = async () => {
    const notRedirect =
      window.location.host === 'localhost:3000' ||
      window.location.host === 'test.accurator.se' ||
      window.location.host === 'app.accurator.se';
    const href = notRedirect ? window.location.href.split('/').slice(0, 3).join('/') : `https://app.accurator.se`;
    if (buttonText === t('button.createAccount', 'Skapa konto')) {
      await logEvent({
        event: 'CLICKED_CREATE_SUPPLIER_ACCOUNT',
        userIdentifier: appState.getUserEmail(),
        metadata: {
          source: 'quotation',
        },
        options: {
          unique: true,
        }
      });
      await logMessage('New supplier account creation button click', SlackChannels.SUPPLIERS);
      window.location.href = `${href}/supplier-sign-up?uuid=${appState.getUserEmail()}`;
    } else if (buttonText === t('button.addPresentation', 'Fyll i presentation')) {
      window.location.href = `${href}/${supplierStatus?.orgSlug}/supplier-presentation`;
    }
  }

  return (
    <div className={styles.card}>
      <div className={styles.container}>
        <p>{t('heading', 'Leverantörskonto')}</p>
        <div className={styles.content}>
          {text}
        </div>
      </div>
      {buttonText && <BtButton color="white" onClick={onClick}>{buttonText}</BtButton>}
    </div>
  )
}
